import * as React from 'react';
import { Store } from 'src/stores/store.interface';
import {
	AttributeEditor,
	Box,
	FormField,
	Icon,
	Input,
	Select,
} from '@amzn/awsui-components-console';
import { useStore } from 'src/stores/store';
import { requiredText } from 'src/utils/CommonUtils';
import { getLabelFromConditionsValuemap } from './conditionalAttributesConstants';
import './conditionalAttributes.scss';

export default function ConditionalAttributes() {
	const conditionalAttributes = useStore(
		(state: Store) => state.activeTaskDefinition.conditionalAttributes
	);
	const isFirstSubmissionDone = useStore(
		(state: Store) => state.isFirstSubmissionDone
	);
	const setConditionalAttribute = useStore(
		(state: Store) => state.setConditionalAttribute
	);

	const onChangeOperand = ({ detail }, item, index) => {
		const updatedItems = [...conditionalAttributes];

		updatedItems[index] = {
			...item,
			operator: detail.selectedOption.value as string,
		};

		setConditionalAttribute(updatedItems);
	};

	return (
		<Box>
			<Box variant="h3" margin={{ bottom: 's' }}>
				<Icon name="filter" variant="subtle" /> Conditional Attributes
			</Box>
			<Box color="text-body-secondary" margin={{ bottom: 's' }}>
				Define conditions that must be met for this task to execute.
			</Box>
			{conditionalAttributes.length > 1 && (
				<Box 
					color="text-status-info" 
					fontSize="body-s" 
					margin={{ bottom: 'm' }}
					padding="s"
					className="conditionalAttributesAndNotice"
				>
					<strong>AND Logic:</strong> All conditions below must be true for this task to execute
				</Box>
			)}

			<AttributeEditor
				onAddButtonClick={() =>
					setConditionalAttribute([
						...conditionalAttributes,
						{ operator: 'eq', attributeName: '', attributeValue: '' },
					])
				}
				onRemoveButtonClick={({ detail: { itemIndex } }) => {
					const tmpItems = [...conditionalAttributes];

					tmpItems.splice(itemIndex, 1);
					setConditionalAttribute(tmpItems);
				}}
				items={conditionalAttributes}
				addButtonText="Add Condition"
				removeButtonText="Remove"
				isItemRemovable={() => true}
				definition={[
					{
						label: 'Attribute Name',
						control: ({ attributeName }, index) => (
							<FormField
								data-testid={`attributeName${index}`}
								errorText={isFirstSubmissionDone && requiredText(attributeName)}
							>
								<Input
									value={attributeName}
									placeholder="Enter attribute name"
									onChange={(event) => {
										const updatedItems = [...conditionalAttributes];

										updatedItems[index] = {
											...updatedItems[index],
											attributeName: event.detail.value,
										};
										setConditionalAttribute(updatedItems);
									}}
								/>
							</FormField>
						),
					},
					{
						label: 'Operand',
						control: (item, index) => (
							<FormField
								data-testid={`operand${index}`}
								errorText={isFirstSubmissionDone && requiredText(item.operator)}
							>
								<Select
									selectedOption={{
										label: getLabelFromConditionsValuemap[item.operator],
										value: item.operator,
									}}
									onChange={(detail) => {
										onChangeOperand(detail, item, index);
									}}
									options={[
										{ label: 'EQUALS', value: 'eq' },
										{ label: 'NOT_EQUALS', value: 'ne' },
										{ label: 'IN', value: 'in' },
										{ label: 'NOT_IN', value: 'ni' },
										{ label: 'CONTAINS', value: 'contains' },
										{ label: 'NOT_CONTAINS', value: 'not_contains' },
									]}
								/>
							</FormField>
						),
					},
					{
						label: 'Attribute Value',
						control: ({ attributeValue }, index) => (
							<FormField
								data-testid={`attributeValue${index}`}
								errorText={
									isFirstSubmissionDone && requiredText(attributeValue)
								}
							>
								<Input
									value={attributeValue}
									placeholder="Enter attribute value"
									onChange={(event) => {
										const updatedItems = [...conditionalAttributes];

										updatedItems[index] = {
											...updatedItems[index],
											attributeValue: event.detail.value,
										};
										setConditionalAttribute(updatedItems);
									}}
								/>
							</FormField>
						),
					},
				]}
				empty={
					<Box textAlign="center" color="text-body-secondary" padding="m">
						No conditions defined. Click "Add Condition" to create conditional execution rules.
					</Box>
				}
			/>
		</Box>
	);
}
